import React, { useState, useCallback } from 'react';
import { User, Phone, MapPin, CreditCard, FileText, Loader2, ArrowLeft, Truck } from 'lucide-react';
import { Input, Select, StructuredAddressForm, emptyAddressForm } from './ui';
import type { AddressFormValues } from './ui';
import { CartItem } from './CartDrawer';
import cardapioAPI, { FreteInfo } from '../services/cardapioAPI';

interface CardapioCheckoutFormProps {
  cartItems: CartItem[];
  meiosPagamento?: string;
  linkId: string;
  onSubmit: (data: CardapioCheckoutFormData) => void;
  onBack: () => void;
  isSubmitting: boolean;
}

export interface CardapioCheckoutFormData {
  nome_cliente: string;
  telefone: string;
  endereco: string;
  meio_pagamento: string;
  troco?: string;
  observacoes?: string;
  retirada_local?: boolean;
  valor_frete?: number;
}

const CardapioCheckoutForm: React.FC<CardapioCheckoutFormProps> = ({
  cartItems,
  meiosPagamento,
  linkId,
  onSubmit,
  onBack,
  isSubmitting,
}) => {
  const [formData, setFormData] = useState<CardapioCheckoutFormData>({
    nome_cliente: '',
    telefone: '',
    endereco: '',
    meio_pagamento: '',
    troco: '',
    observacoes: '',
    retirada_local: false,
  });

  const [errors, setErrors] = useState<Partial<CardapioCheckoutFormData>>({});
  const [addressErrors, setAddressErrors] = useState<Partial<Record<keyof AddressFormValues, string>>>({});
  const [freteInfo, setFreteInfo] = useState<Partial<FreteInfo>>({});
  const [calculatingFrete, setCalculatingFrete] = useState(false);
  const [addressFields, setAddressFields] = useState<AddressFormValues>(emptyAddressForm);

  // Formatar telefone para exibicao
  const formatPhone = (value: string): string => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 7) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    } else if (numbers.length <= 11) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
    }
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  // Validar telefone brasileiro
  const validatePhone = (phone: string): boolean => {
    const numbers = phone.replace(/\D/g, '');
    return numbers.length >= 10 && numbers.length <= 11;
  };

  // Helper function to normalize payment method names with emojis
  const normalizePaymentMethod = (method: string): string => {
    const normalized = method.trim().toLowerCase();

    const paymentMap: Record<string, string> = {
      'dinheiro': 'Dinheiro',
      'pix': 'PIX',
      'cartao_debito': 'Cartao de Debito',
      'cartao_credito': 'Cartao de Credito',
      'cartao de debito': 'Cartao de Debito',
      'cartao de credito': 'Cartao de Credito',
      'debito': 'Cartao de Debito',
      'credito': 'Cartao de Credito',
      'cartao': 'Cartao',
      'vr': 'Vale Refeicao (VR)',
      'va': 'Vale Alimentacao (VA)',
    };

    return paymentMap[normalized] || method.trim();
  };

  // Parse meios de pagamento from string
  const paymentMethodsList = meiosPagamento
    ? meiosPagamento.split(',').map(m => m.trim()).filter(m => m)
    : ['Dinheiro', 'PIX', 'Cartao de Debito', 'Cartao de Credito'];

  const paymentMethods = paymentMethodsList.map(method => ({
    value: method,
    label: normalizePaymentMethod(method)
  }));

  const calculateSubtotal = (): number => {
    return cartItems.reduce((total, item) => {
      const itemPrice = item.finalPrice || item.product.price;
      return total + (itemPrice * item.quantity);
    }, 0);
  };

  const subtotal = calculateSubtotal();
  const valorFrete = formData.retirada_local ? 0 : (freteInfo.valor_frete || 0);
  const total = subtotal + valorFrete;
  const showTrocoField = formData.meio_pagamento.toLowerCase() === 'dinheiro';

  const validateForm = (): boolean => {
    const newErrors: Partial<CardapioCheckoutFormData> = {};

    if (!formData.nome_cliente.trim()) {
      newErrors.nome_cliente = 'Nome e obrigatorio';
    }

    if (!formData.telefone.trim()) {
      newErrors.telefone = 'Telefone e obrigatorio';
    } else if (!validatePhone(formData.telefone)) {
      newErrors.telefone = 'Telefone invalido. Use formato: (11) 99999-9999';
    }

    if (!formData.retirada_local) {
      const addrErrors: Partial<Record<keyof AddressFormValues, string>> = {};
      if (!addressFields.rua.trim()) addrErrors.rua = 'Obrigatorio';
      if (!addressFields.numero.trim()) addrErrors.numero = 'Obrigatorio';
      if (!addressFields.bairro.trim()) addrErrors.bairro = 'Obrigatorio';
      if (!addressFields.cidade.trim()) addrErrors.cidade = 'Obrigatorio';
      if (!addressFields.estado.trim()) addrErrors.estado = 'Obrigatorio';
      setAddressErrors(addrErrors);
      if (Object.keys(addrErrors).length > 0) {
        newErrors.endereco = 'Preencha todos os campos obrigatorios do endereco';
      }
    }

    if (!formData.meio_pagamento) {
      newErrors.meio_pagamento = 'Selecione uma forma de pagamento';
    }

    if (
      formData.meio_pagamento.toLowerCase() === 'dinheiro' &&
      formData.troco &&
      parseFloat(formData.troco) < total
    ) {
      newErrors.troco = 'Valor do troco deve ser maior que o total';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      // Construir endereco completo a partir dos campos estruturados
      let endereco = formData.endereco;
      if (formData.retirada_local) {
        endereco = 'RETIRAR NO LOCAL';
      } else if (addressFields.rua) {
        endereco = `${addressFields.rua}, ${addressFields.numero} - ${addressFields.bairro}, ${addressFields.cidade} - ${addressFields.estado}`;
      }
      onSubmit({
        ...formData,
        endereco,
        valor_frete: valorFrete
      });
    }
  };

  const handleChange = (field: keyof CardapioCheckoutFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handlePhoneChange = (value: string) => {
    const formatted = formatPhone(value);
    handleChange('telefone', formatted);
  };

  const calculateFreteForAddress = useCallback(async (
    endereco: string,
    structured?: { bairro?: string; cidade?: string; estado?: string }
  ) => {
    if (!endereco.trim() || !linkId) {
      setFreteInfo({});
      return;
    }

    setCalculatingFrete(true);
    setFreteInfo({});

    try {
      const data = await cardapioAPI.calculateFrete(linkId, endereco, structured);
      setFreteInfo(data);

      if (!data.can_deliver) {
        setErrors(prev => ({
          ...prev,
          endereco: `Endereço fora da área de entrega (max. ${data.max_distance}km)`
        }));
      }
    } catch (err: any) {
      console.error('Erro ao calcular frete:', err);
      setFreteInfo({});
    } finally {
      setCalculatingFrete(false);
    }
  }, [linkId]);

  const handleAddressFieldChange = (field: keyof AddressFormValues, value: string) => {
    setAddressFields(prev => ({ ...prev, [field]: value }));
    if (addressErrors[field]) {
      setAddressErrors(prev => ({ ...prev, [field]: undefined }));
    }
    // Clear general endereco error
    if (errors.endereco) {
      setErrors(prev => ({ ...prev, endereco: undefined }));
    }
  };

  // Calcular frete quando todos os campos obrigatorios estiverem preenchidos
  const handleCalculateFreteFromFields = useCallback(() => {
    if (addressFields.rua && addressFields.numero && addressFields.bairro && addressFields.cidade && addressFields.estado) {
      const fullAddress = `${addressFields.rua}, ${addressFields.numero} - ${addressFields.bairro}, ${addressFields.cidade} - ${addressFields.estado}`;
      handleChange('endereco', fullAddress);
      calculateFreteForAddress(fullAddress, {
        bairro: addressFields.bairro,
        cidade: addressFields.cidade,
        estado: addressFields.estado
      });
    }
  }, [addressFields, calculateFreteForAddress]);

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-[4px] transition-colors"
              disabled={isSubmitting}
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-[22px] font-extrabold tracking-tight text-gray-900">
              Finalizar Pedido
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Resumo do Pedido */}
          <div className="bg-white p-[20px] rounded-[4px] border-[1.5px] border-gray-900">
            <h2 className="text-[22px] font-extrabold tracking-tight text-gray-900 mb-4">
              Resumo do Pedido
            </h2>
            <div className="space-y-2">
              {cartItems.map((item, index) => {
                const itemPrice = item.finalPrice || item.product.price;
                const displayName = (item as any).displayName || item.product.name;
                const itemKey = JSON.stringify({
                  productId: item.product.id,
                  attributes: item.product_variables || {},
                  index
                });
                return (
                  <div
                    key={itemKey}
                    className="flex justify-between text-[14px]"
                  >
                    <span className="text-gray-700">
                      {item.quantity}x {displayName}
                    </span>
                    <span className="font-semibold text-gray-900">
                      R$ {(itemPrice * item.quantity).toFixed(2).replace('.', ',')}
                    </span>
                  </div>
                );
              })}
              <div className="border-t border-gray-200 pt-2 mt-2 space-y-1">
                <div className="flex justify-between text-[14px]">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="text-gray-900">
                    R$ {subtotal.toFixed(2).replace('.', ',')}
                  </span>
                </div>
                {valorFrete > 0 && (
                  <div className="flex justify-between text-[14px]">
                    <span className="text-gray-600">Frete</span>
                    <span className="text-gray-900">
                      R$ {valorFrete.toFixed(2).replace('.', ',')}
                    </span>
                  </div>
                )}
                <div className="flex justify-between font-semibold text-[18px] pt-1">
                  <span className="text-gray-900">Total</span>
                  <span style={{ color: 'var(--text-color)' }}>
                    R$ {total.toFixed(2).replace('.', ',')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Dados Pessoais */}
          <div className="bg-white p-[20px] rounded-[4px] border-[1.5px] border-gray-900">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-5 h-5" style={{ stroke: 'var(--text-color)' }} />
              <h2 className="text-[22px] font-extrabold tracking-tight text-gray-900">
                Seus Dados
              </h2>
            </div>

            <div className="space-y-4">
              <Input
                label="Nome Completo *"
                type="text"
                value={formData.nome_cliente}
                onChange={(e: any) => handleChange('nome_cliente', e.target.value)}
                placeholder="Digite seu nome completo"
                error={errors.nome_cliente}
              />

              <div>
                <label className="label text-[15px] flex items-center gap-2">
                  <Phone className="w-4 h-4" style={{ stroke: 'var(--text-color)' }} />
                  Telefone (WhatsApp) *
                </label>
                <input
                  type="tel"
                  value={formData.telefone}
                  onChange={(e: any) => handlePhoneChange(e.target.value)}
                  placeholder="(11) 99999-9999"
                  className={`input ${errors.telefone ? 'border-error-500' : ''}`}
                  maxLength={16}
                />
                {errors.telefone && (
                  <p className="text-error-500 text-sm mt-1">{errors.telefone}</p>
                )}
              </div>
            </div>
          </div>

          {/* Endereco */}
          <div className="bg-white p-[20px] rounded-[4px] border-[1.5px] border-gray-900">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-5 h-5" style={{ stroke: 'var(--text-color)' }} />
              <h2 className="text-[22px] font-extrabold tracking-tight text-gray-900">
                Endereço de Entrega
              </h2>
            </div>

            {/* Checkbox Retirada no Local */}
            <div className="flex items-center gap-3 p-4 rounded-lg mb-4" style={{ backgroundColor: 'var(--soft-color)', borderWidth: '1px', borderStyle: 'solid', borderColor: 'var(--text-color)' }}>
              <input
                type="checkbox"
                id="retirada_local"
                checked={formData.retirada_local}
                onChange={(e: any) => {
                  const isChecked = e.target.checked;
                  setFormData(prev => ({
                    ...prev,
                    retirada_local: isChecked,
                    endereco: isChecked ? '' : prev.endereco
                  }));
                  if (isChecked) {
                    setErrors(prev => ({ ...prev, endereco: undefined }));
                    setAddressErrors({});
                    setFreteInfo({});
                    setAddressFields(emptyAddressForm);
                  }
                }}
                className="w-4 h-4 bg-gray-100 border-gray-300 rounded focus:ring-2"
                style={{ accentColor: 'var(--bg-color)' }}
              />
              <label htmlFor="retirada_local" className="text-sm font-medium cursor-pointer" style={{ color: 'var(--badge-color)' }}>
                Retirada no local (sem entrega)
              </label>
            </div>

            {!formData.retirada_local && (
              <>
                <StructuredAddressForm
                  values={addressFields}
                  onChange={handleAddressFieldChange}
                  errors={addressErrors}
                  disabled={isSubmitting}
                  showApelido={false}
                />

                {/* Botao Calcular Frete */}
                <button
                  type="button"
                  onClick={handleCalculateFreteFromFields}
                  disabled={
                    calculatingFrete ||
                    !addressFields.rua.trim() ||
                    !addressFields.numero.trim() ||
                    !addressFields.bairro.trim() ||
                    !addressFields.cidade.trim() ||
                    !addressFields.estado.trim()
                  }
                  className="w-full btn btn-order-link text-[13px] py-2.5 flex items-center justify-center gap-2 mt-2"
                >
                  {calculatingFrete ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Calculando frete...
                    </>
                  ) : (
                    <>
                      <Truck className="w-4 h-4" />
                      Calcular frete
                    </>
                  )}
                </button>

                {/* Exibicao do Frete */}
                {!calculatingFrete && freteInfo.valor_frete !== undefined && freteInfo.can_deliver && (
                  <div className="mt-2 p-4 rounded-lg" style={{ backgroundColor: 'var(--soft-color)' }}>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <Truck className="w-5 h-5" style={{ color: 'var(--text-color)' }} />
                        <span className="font-medium" style={{ color: 'var(--text-color)' }}>
                          Frete
                        </span>
                      </div>
                      <span className="text-lg font-bold" style={{ color: 'var(--text-color)' }}>
                        R$ {freteInfo.valor_frete.toFixed(2).replace('.', ',')}
                      </span>
                    </div>
                    {freteInfo.distancia_km && (
                      <p className="text-sm text-gray-600 mt-1">
                        Distancia: {freteInfo.distancia_km.toFixed(1)} km
                      </p>
                    )}
                  </div>
                )}

                {!calculatingFrete && freteInfo.can_deliver === false && (
                  <div className="mt-2 p-3 rounded-lg bg-error-50 border border-error-200">
                    <p className="text-sm text-error-700">
                      Endereço fora da área de entrega (max. {freteInfo.max_distance}km)
                    </p>
                  </div>
                )}
              </>
            )}

            {formData.retirada_local && (
              <div className="p-4 bg-success-50 border border-success-200 rounded-lg">
                <div className="text-sm text-success-800 space-y-2">
                  <p>Você irá retirar o pedido no local</p>
                  <p>Não haverá cobrança de frete</p>
                </div>
              </div>
            )}
          </div>

          {/* Pagamento */}
          <div className="bg-white p-[20px] rounded-[4px] border-[1.5px] border-gray-900">
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="w-5 h-5" style={{ stroke: 'var(--text-color)' }} />
              <h2 className="text-[22px] font-extrabold tracking-tight text-gray-900">
                Forma de Pagamento
              </h2>
            </div>

            <div className="space-y-4">
              <div>
                <Select
                  label="Meio de Pagamento *"
                  value={formData.meio_pagamento}
                  onChange={(value: any) => handleChange('meio_pagamento', value)}
                  options={paymentMethods}
                  placeholder="Selecione..."
                  error={errors.meio_pagamento}
                  className={errors.meio_pagamento ? 'border-error-500' : ''}
                />
              </div>

              {showTrocoField && (
                <div>
                  <Input
                    label="Troco para quanto?"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.troco}
                    onChange={(e: any) => {
                      const value = e.target.value;
                      if (value === '' || parseFloat(value) >= 0) {
                        handleChange('troco', value);
                      }
                    }}
                    placeholder="Ex: 50.00"
                    error={errors.troco}
                    helperText={`Total do pedido: R$ ${total.toFixed(2).replace('.', ',')}${valorFrete > 0 ? ' (inclui frete)' : ''}`}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Observacoes */}
          <div className="bg-white p-[20px] rounded-[4px] border-[1.5px] border-gray-900">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5" style={{ stroke: 'var(--text-color)' }} />
              <h2 className="text-[22px] font-extrabold tracking-tight text-gray-900">
                Observacoes
              </h2>
            </div>

            <div>
              <label className="label text-[15px]">Observacoes (opcional)</label>
              <textarea
                value={formData.observacoes}
                onChange={(e: any) => handleChange('observacoes', e.target.value)}
                placeholder="Alguma observacao sobre seu pedido?"
                rows={3}
                className="input resize-none"
              />
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full btn btn-order-link text-[15px] py-4"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Processando...
              </>
            ) : (
              <>
                Confirmar Pedido
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CardapioCheckoutForm;
